import React from "react";
import { Form, InputGroup, Table } from "react-bootstrap";
import { getBillTaxBreakdownRows } from "../../../../utils/financeEngine";
import "../../NewSales/components/CartSummary.scss";

const isGstTaxMode = (posTaxType) => String(posTaxType || "").toLowerCase() === "gst";

export const CartSummary = ({
    t,
    discountType,
    onDiscountTypeChange,
    discountValue,
    onDiscountValueChange,
    totals,
    getCurrencySymbol,
    disableScrollProps,
    posTaxType,
    pharmacyStateCode,
    patientStateCode,
    cart,
    itemTaxes,
    scrollHeight,
    showReturnTax = true,
}) => {
    const isSameState =
        !patientStateCode ||
        !pharmacyStateCode ||
        pharmacyStateCode === patientStateCode;

    const taxBreakdown = !cart || !itemTaxes
        ? []
        : getBillTaxBreakdownRows(
            cart,
            itemTaxes,
            totals,
            posTaxType,
            isSameState,
            "return",
            discountType,
            discountValue
        );

    const totalTaxableBeforeDiscountFromBreakdown = taxBreakdown.reduce(
        (sum, g) => sum + (g.taxableAmountBeforeDiscount || 0),
        0
    );
    const totalTaxableFromBreakdown = taxBreakdown.reduce(
        (sum, g) => sum + g.taxableAmount,
        0
    );
    const netTaxableFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.netTaxableAmount, 0);
    const totalCGSTFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.CGST, 0);
    const totalSGSTFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.SGST, 0);
    const totalIGSTFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.IGST, 0);
    const totalVATFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.VAT, 0);
    const totalTaxFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.totalTax, 0);
    const totalAmountFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.totalAmount, 0);

    const hasTaxRatesInCart = showReturnTax && cart.some((item) => Number(item.taxPercentage) > 0);
    const hasTax =
        showReturnTax &&
        cart.length > 0 &&
        (totals.tax > 0 || hasTaxRatesInCart) &&
        (taxBreakdown.length > 0 || hasTaxRatesInCart);
    const showSimpleSummary = cart.length > 0 && !hasTax;

    const renderDiscountField = () => (
        <>
            <Form.Label className="mb-0 text-nowrap">
                {t("Discount")} {discountType === "Percentage" && "(%)"}
            </Form.Label>

            {discountType === "Percentage" ? (
                <InputGroup size="sm" className="pos-cart-summary__discount-input">
                    <Form.Control
                        type="number"
                        value={discountValue}
                        min={0}
                        max={100}
                        onChange={(event) => {
                            if (Number(event.target.value) > 0 && Number(event.target.value) <= 100) {
                                onDiscountValueChange(event.target.value);
                            } else {
                                onDiscountValueChange(0);
                            }
                        }}
                        {...disableScrollProps}
                    />
                    <InputGroup.Text>
                        {getCurrencySymbol()}
                        {totals.discount.toFixed(2)}
                    </InputGroup.Text>
                </InputGroup>
            ) : (
                <Form.Control
                    type="number"
                    size="sm"
                    className="pos-cart-summary__discount-input"
                    min={0}
                    value={discountValue}
                    onChange={(event) => {
                        if (Number(event.target.value) > 0) {
                            onDiscountValueChange(event.target.value);
                        } else {
                            onDiscountValueChange(0);
                        }
                    }}
                    {...disableScrollProps}
                />
            )}
        </>
    );

    const renderTotalsTable = ({ useTaxLabels = true } = {}) => (
        <Table bordered size="sm" className="pos-cart-summary__totals-table mb-0">
            <tbody>
                <tr>
                    <td className="text-end">
                        {useTaxLabels ? t("Total Return Taxable Value") : t("Subtotal")}
                    </td>
                    <td className="text-end fw-bold">
                        {getCurrencySymbol()}
                        {(useTaxLabels
                            ? totalTaxableBeforeDiscountFromBreakdown
                            : totals.subtotal
                        ).toFixed(2)}
                    </td>
                </tr>
                {totals.discount > 0 && (
                    <>
                        <tr>
                            <td className="text-end">{t("Discount")}</td>
                            <td className="text-end text-danger">
                                -{getCurrencySymbol()}{totals.discount.toFixed(2)}
                            </td>
                        </tr>
                        <tr>
                            <td className="text-end">
                                {useTaxLabels ? t("Net Taxable Value") : t("Net Amount")}
                            </td>
                            <td className="text-end fw-bold">
                                {getCurrencySymbol()}
                                {(useTaxLabels
                                    ? netTaxableFromBreakdown
                                    : totals.subtotal - totals.discount
                                ).toFixed(2)}
                            </td>
                        </tr>
                    </>
                )}
                {useTaxLabels && isGstTaxMode(posTaxType) && isSameState && totalCGSTFromBreakdown > 0 && (
                    <tr>
                        <td className="text-end">Refund: CGST</td>
                        <td className="text-end text-danger">
                            -{getCurrencySymbol()}{totalCGSTFromBreakdown.toFixed(2)}
                        </td>
                    </tr>
                )}
                {useTaxLabels && isGstTaxMode(posTaxType) && isSameState && totalSGSTFromBreakdown > 0 && (
                    <tr>
                        <td className="text-end">Refund: SGST</td>
                        <td className="text-end text-danger">
                            -{getCurrencySymbol()}{totalSGSTFromBreakdown.toFixed(2)}
                        </td>
                    </tr>
                )}
                {useTaxLabels && isGstTaxMode(posTaxType) && !isSameState && totalIGSTFromBreakdown > 0 && (
                    <tr>
                        <td className="text-end">Refund: IGST</td>
                        <td className="text-end text-danger">
                            -{getCurrencySymbol()}{totalIGSTFromBreakdown.toFixed(2)}
                        </td>
                    </tr>
                )}
                {useTaxLabels && !isGstTaxMode(posTaxType) && totalVATFromBreakdown > 0 && (
                    <tr>
                        <td className="text-end">Refund: VAT</td>
                        <td className="text-end text-danger">
                            -{getCurrencySymbol()}{totalVATFromBreakdown.toFixed(2)}
                        </td>
                    </tr>
                )}
                {useTaxLabels && totalTaxFromBreakdown > 0 && (
                    <tr className="table-warning">
                        <td className="text-end fw-bold">{t("Total Tax Refund")}</td>
                        <td className="text-end fw-bold text-danger">
                            -{getCurrencySymbol()}{totalTaxFromBreakdown.toFixed(2)}
                        </td>
                    </tr>
                )}
                <tr className="table-danger">
                    <td className="text-end fw-bold">{t("Return Amount")}</td>
                    <td className="text-end fw-bold text-danger">
                        {getCurrencySymbol()}
                        {(useTaxLabels ? totalAmountFromBreakdown : totals.netTotal).toFixed(2)}
                    </td>
                </tr>
            </tbody>
        </Table>
    );

    return (
        <div className={`pos-cart-summary${showSimpleSummary ? " pos-cart-summary--compact" : ""}`}>
            <div className="pos-cart-summary__discount">
                {renderDiscountField()}
                {showSimpleSummary && (
                    <div className="d-flex align-items-center gap-2 ms-auto">
                        <h6 className="pos-cart-summary__heading mb-0">{t("Return Amount")}</h6>
                        <span className="text-end fw-bold fs-6 text-danger">
                            {getCurrencySymbol()}{totals.netTotal.toFixed(2)}
                        </span>
                    </div>
                )}
            </div>

            <div
                className="pos-cart-summary__scroll"
                style={
                    scrollHeight
                        ? { height: scrollHeight, maxHeight: scrollHeight, minHeight: scrollHeight }
                        : undefined
                }
            >
                {cart.length === 0 && (
                    <p className="pos-cart-summary__empty">
                        {t("Add items to view summary and totals")}
                    </p>
                )}

                {showSimpleSummary && (
                    <div className="pos-cart-summary__simple-table">
                        <h6 className="pos-cart-summary__heading">{t("Return Summary")}</h6>
                        {renderTotalsTable({ useTaxLabels: false })}
                    </div>
                )}

                {hasTax && (
                    <div className="pos-cart-summary__row">
                        <div className="pos-cart-summary__tax-col">
                            <h6 className="pos-cart-summary__heading">{t("Return Tax Summary")}</h6>
                            <Table bordered size="sm" className="pos-cart-summary__tax-table mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="text-center">{t("Tax %")}</th>
                                        <th className="text-end">{t("Taxable Value")}</th>
                                        {isGstTaxMode(posTaxType) ? (
                                            isSameState ? (
                                                <>
                                                    <th className="text-end">{t("CGST Amt")}</th>
                                                    <th className="text-end">{t("SGST Amt")}</th>
                                                </>
                                            ) : (
                                                <th className="text-end">{t("IGST Amt")}</th>
                                            )
                                        ) : (
                                            <th className="text-end">{t("VAT Amt")}</th>
                                        )}
                                        <th className="text-end">{t("Total Tax")}</th>
                                        <th className="text-end">{t("Return Amount")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {taxBreakdown.map((group, index) => (
                                        group.taxRate > 0 && (
                                            <tr key={index}>
                                                <td className="text-center fw-bold">{group.taxRate}%</td>
                                                <td className="text-end">
                                                    {getCurrencySymbol()}{group.taxableAmount.toFixed(2)}
                                                </td>
                                                {isGstTaxMode(posTaxType) ? (
                                                    isSameState ? (
                                                        <>
                                                            <td className="text-end text-success">
                                                                {getCurrencySymbol()}{group.CGST.toFixed(2)}
                                                            </td>
                                                            <td className="text-end text-success">
                                                                {getCurrencySymbol()}{group.SGST.toFixed(2)}
                                                            </td>
                                                        </>
                                                    ) : (
                                                        <td className="text-end text-success">
                                                            {getCurrencySymbol()}{group.IGST.toFixed(2)}
                                                        </td>
                                                    )
                                                ) : (
                                                    <td className="text-end text-success">
                                                        {getCurrencySymbol()}{group.VAT.toFixed(2)}
                                                    </td>
                                                )}
                                                <td className="text-end fw-bold text-success">
                                                    {getCurrencySymbol()}{group.totalTax.toFixed(2)}
                                                </td>
                                                <td className="text-end fw-bold">
                                                    {getCurrencySymbol()}{group.totalAmount.toFixed(2)}
                                                </td>
                                            </tr>
                                        )
                                    ))}
                                    <tr className="table-primary fw-bold">
                                        <td className="text-center">{t("Total")}</td>
                                        <td className="text-end">
                                            {getCurrencySymbol()}{totalTaxableFromBreakdown.toFixed(2)}
                                        </td>
                                        {isGstTaxMode(posTaxType) ? (
                                            isSameState ? (
                                                <>
                                                    <td className="text-end">
                                                        {getCurrencySymbol()}{totalCGSTFromBreakdown.toFixed(2)}
                                                    </td>
                                                    <td className="text-end">
                                                        {getCurrencySymbol()}{totalSGSTFromBreakdown.toFixed(2)}
                                                    </td>
                                                </>
                                            ) : (
                                                <td className="text-end">
                                                    {getCurrencySymbol()}{totalIGSTFromBreakdown.toFixed(2)}
                                                </td>
                                            )
                                        ) : (
                                            <td className="text-end">
                                                {getCurrencySymbol()}{totalVATFromBreakdown.toFixed(2)}
                                            </td>
                                        )}
                                        <td className="text-end text-success">
                                            {getCurrencySymbol()}{totalTaxFromBreakdown.toFixed(2)}
                                        </td>
                                        <td className="text-end">
                                            {getCurrencySymbol()}{totalAmountFromBreakdown.toFixed(2)}
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </div>

                        <div className="pos-cart-summary__totals-col">
                            {renderTotalsTable({ useTaxLabels: true })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CartSummary;
